<!--
  A failure's request (what failed, its status, the time and the request
  ID) behind a collapsed "Details" disclosure, with Copy details for
  sending it to support. A failure that is a bug in Changeling (its engine's)
  also offers Send feedback, starting the note with what happened and the
  details.
-->
<script lang="ts">
  import { copyText } from '../clipboard';
  import { formatDetails, type Failure } from '../api/failure';
  import { feedbackUi } from '../feedback/feedback.svelte';

  interface Props {
    failure: Failure;
    /** What was being worked out, for the note Send feedback starts: "src/a.rs". */
    what?: string;
    /** A line to add to the details, about how the failure is shown again. */
    note?: string;
  }
  let { failure, what, note }: Props = $props();

  const text = $derived(note ? `${formatDetails(failure)}\n${note}` : formatDetails(failure));
  let copied: 'no' | 'yes' | 'failed' = $state('no');
  // A new failure (each retry's) has not been copied.
  $effect.pre(() => {
    void failure;
    copied = 'no';
  });

  async function copy() {
    copied = (await copyText(text)) ? 'yes' : 'failed';
  }
</script>

<details class="failure-details">
  <summary>Details</summary>
  <pre>{text}</pre>
  <div class="failure-copy">
    <button class="btn small" type="button" onclick={copy}>Copy details</button>
    {#if failure.cause === 'engine_bug'}
      <button class="btn small" type="button" onclick={() => feedbackUi.show(`Changeling hit a bug working out ${what ?? 'something'}.\n\n${text}`)}>Send feedback</button>
    {/if}
    <span class="muted" role="status">{copied === 'yes' ? 'Copied.' : copied === 'failed' ? "Couldn't copy; select the text above instead." : ''}</span>
  </div>
</details>

<style>
  .failure-details {
    text-align: left;
    font-size: 13px;
    color: var(--muted);
    max-width: 100%;
  }
  .failure-details summary {
    cursor: pointer;
    width: max-content;
  }
  pre {
    margin: 8px 0;
    padding: 10px 12px;
    background: var(--code);
    color: var(--fg);
    border-radius: 6px;
    font: 12px/1.5 ui-monospace, SFMono-Regular, Consolas, monospace;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    user-select: all;
  }
  .failure-copy {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }
</style>
